/**
 * [TD-47.6c-E.2] BidConfidenceSlider - v1.6 compact horizontal single-row.
 *
 *   [░░░░░░◯░░░░░░░]  MAX BID $24  STRONG BUY
 *
 * Founder Feb 2026 redesign: single-row compact bar (max-w 280px, mx-auto)
 * so the bid buttons in CompactActionPanel stay unobstructed. Matches the
 * inline "trading-desk" style from the founder's reference image.
 *
 * Track gradient green→yellow→red. HIGH confidence = LEFT (green) per
 * Spec §5.3 line 411.
 *
 * Per R3.B - reads only structured {score, label, maxBid, reasonCode}.
 * Per A.7 - template copy only.
 */
import { useState } from "react";
import { useTheme } from "../../contexts/ThemeContext";

const TRACK_GRADIENT = (
  "linear-gradient(to right, " +
  "#00C853 0%, #00C853 35%, " +
  "#F5C518 50%, " +
  "#E03030 75%, #B71C1C 100%)"
);

const REASON_STEM = {
  STRONG_BUY: "PGF likes this. You're getting value below the league mark.",
  BUY: "Reasonable price relative to PGF Value.",
  HOLD: "On target. Neither steal nor stretch.",
  PASS: "Bid is above PGF Value.",
  STRONG_PASS: "PGF flags this as a stretch.",
};
const REASON_OVERLAYS = {
  OVERPAY: "Current bid is above PGF Value.",
  UNDERVALUE: "Current bid is well below PGF Value.",
  POSITION_NEED: "Fills a primary roster need.",
  POSITION_SATURATED: "Position already saturated on your roster.",
  BUDGET_TIGHT: "Your budget runway is tight at this bid.",
  BUDGET_FLEX: "You have budget headroom.",
  ON_TARGET: "Factors balance out.",
};

export function defaultTooltipFor({ label, reasonCode, maxBid }) {
  const stem = REASON_STEM[(label || "").replace(/\s+/g, "_")] || "";
  const overlay = REASON_OVERLAYS[reasonCode] || "";
  const maxLine = maxBid > 0 ? ` Max bid $${maxBid}.` : "";
  return `${stem} ${overlay}${maxLine}`.trim();
}

export function BidConfidenceSlider({
  bcScore = 0,
  bcLabel = "-",
  maxBid = 0,
  reasonCode = "ON_TARGET",
  tooltip,
}) {
  const { isDark } = useTheme();
  const [hover, setHover] = useState(false);

  const titleColor = isDark ? "#F5F5F5" : "#0A0A0A";
  const subColor = isDark ? "rgba(255,255,255,0.65)" : "rgba(0,0,0,0.62)";
  const trackBorder = isDark ? "#2E2E2E" : "#D0D0D0";

  const clamped = Math.max(0, Math.min(1, Number(bcScore) || 0));
  const needleLeftPct = (1 - clamped) * 100;

  const resolvedTooltip = tooltip || defaultTooltipFor({
    label: bcLabel, reasonCode, maxBid,
  });

  return (
    <div
      data-testid="bid-confidence-slider"
      data-bc-label={bcLabel}
      data-bc-reason={reasonCode}
      className="relative flex items-center gap-2 select-none mx-auto"
      style={{ maxWidth: 280, width: "100%" }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {/* Track + needle - inline, shorter track */}
      <div
        className="relative h-2 rounded-full flex-1"
        style={{
          background: TRACK_GRADIENT,
          border: `1px solid ${trackBorder}`,
          minWidth: 80,
        }}
      >
        <div
          data-testid="bid-confidence-needle"
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 rounded-full"
          style={{
            left: `${needleLeftPct}%`,
            width: 12,
            height: 12,
            background: "#FFFFFF",
            border: "2px solid #0A0A0A",
            boxShadow: "0 1px 3px rgba(0,0,0,0.55)",
            transition: "left 280ms cubic-bezier(0.22, 0.61, 0.36, 1)",
          }}
        />
      </div>

      {/* MAX BID - inline mono */}
      <div
        data-testid="bid-confidence-max-bid"
        className="font-mono text-[10px] uppercase tracking-wider whitespace-nowrap shrink-0"
        style={{ color: subColor }}
      >
        Max <span style={{ color: titleColor }}>${maxBid}</span>
      </div>

      {/* Bucket label - inline */}
      <div
        data-testid="bid-confidence-label"
        className="font-display font-black text-[10px] uppercase tracking-widest whitespace-nowrap shrink-0"
        style={{ color: titleColor }}
      >
        {bcLabel}
      </div>

      {/* Hover tooltip */}
      {hover && (
        <div
          data-testid="bid-confidence-tooltip"
          role="tooltip"
          className="absolute z-20 rounded-md px-2 py-1.5 font-body text-[11px] leading-snug shadow-lg"
          style={{
            top: "100%",
            marginTop: 6,
            left: 0,
            maxWidth: 260,
            background: isDark ? "#1C1C1C" : "#FFFFFF",
            color: titleColor,
            border: `1px solid ${trackBorder}`,
            boxShadow: isDark ? "0 4px 12px rgba(0,0,0,0.5)" : "0 4px 12px rgba(0,0,0,0.15)",
          }}
        >
          {resolvedTooltip}
        </div>
      )}
    </div>
  );
}

export default BidConfidenceSlider;
